import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaEdit, FaFilePdf, FaSearch, FaExclamationTriangle, FaCheck, FaChevronDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import InvoiceService, { Invoice, InvoiceStatus, InvoiceKind, INVOICE_STATUS_OPTIONS } from '../services/InvoiceService';
import BusinessPartnerService, { BusinessPartner } from '../../contacts/services/BusinessPartnerService';

const BULK_STATUS_OPTIONS: { value: InvoiceStatus; label: string }[] = [
  { value: 'SENT', label: 'Mark as Sent' },
  { value: 'PAID', label: 'Mark as Paid' },
  { value: 'CANCELLED', label: 'Cancel' },
];

type StatusFilterOption = InvoiceStatus | 'OVERDUE';

const isOverdue = (invoice: Invoice): boolean => {
  if (invoice.status === 'PAID' || invoice.status === 'CANCELLED') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(invoice.due_date);
  dueDate.setHours(0, 0, 0, 0);
  return dueDate < today;
};

export default function InvoicesListTab(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [kindFilter, setKindFilter] = useState<'' | InvoiceKind>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const filterParam = searchParams.get('filter');
  const statusParam = searchParams.get('status');
  const getInitialStatuses = (): StatusFilterOption[] => {
    if (filterParam === 'overdue') return ['OVERDUE'];
    if (statusParam && ['DRAFT', 'SENT', 'PAID', 'CANCELLED'].includes(statusParam)) {
      return [statusParam as InvoiceStatus];
    }
    return [];
  };
  const [statusFilters, setStatusFilters] = useState<StatusFilterOption[]>(getInitialStatuses());
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<InvoiceStatus | ''>('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      let data: Invoice[];

      if (statusFilters.length === 0) {
        // No filter - show all
        data = await InvoiceService.list({
          search: searchTerm || undefined,
          kind: kindFilter || undefined,
        });
      } else if (statusFilters.length === 1 && statusFilters[0] === 'OVERDUE') {
        // Only overdue filter
        data = await InvoiceService.getOverdue();
      } else {
        // Multiple status filters - fetch all and filter client-side
        const allData = await InvoiceService.list({
          search: searchTerm || undefined,
          kind: kindFilter || undefined,
        });

        const includesOverdue = statusFilters.includes('OVERDUE');
        const statusOnly = statusFilters.filter((s) => s !== 'OVERDUE') as InvoiceStatus[];

        data = allData.filter((invoice) => {
          const matchesStatus = statusOnly.length === 0 || statusOnly.includes(invoice.status);
          const isOverdueInvoice = includesOverdue && isOverdue(invoice);
          return matchesStatus || isOverdueInvoice;
        });
      }

      // Filter by date range
      if (dateFrom || dateTo) {
        data = data.filter((invoice) => {
          const dateStr = invoice.issue_date;
          if (!dateStr) return false;
          const invoiceDate = new Date(dateStr);
          invoiceDate.setHours(0, 0, 0, 0);

          if (dateFrom) {
            const from = new Date(dateFrom);
            from.setHours(0, 0, 0, 0);
            if (invoiceDate < from) return false;
          }
          if (dateTo) {
            const to = new Date(dateTo);
            to.setHours(23, 59, 59, 999);
            if (invoiceDate > to) return false;
          }
          return true;
        });
      }

      setInvoices(data);
    } catch (err) {
      console.error('Failed to load invoices:', err);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilters, kindFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      loadInvoices();
    }
  };

  const handleView = (invoice: Invoice) => {
    navigate(`/invoices/${invoice.id}`);
  };

  const handleEdit = (invoice: Invoice, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/invoices/${invoice.id}/edit`);
  };

  const handleDownloadPdf = async (invoice: Invoice, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const blob = await InvoiceService.downloadPdf(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      toast.error('Failed to download PDF');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(invoices.map((i) => i.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (invoiceId: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, invoiceId]);
    } else {
      setSelectedIds(selectedIds.filter((id) => id !== invoiceId));
    }
  };

  const handleBulkStatusChange = async () => {
    if (!bulkStatus) {
      toast.error('Please select a status');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Please select at least one invoice');
      return;
    }

    setBulkProcessing(true);
    try {
      const result = await InvoiceService.bulkChangeStatus(selectedIds, bulkStatus);

      if (result.updated_count === result.total_requested) {
        toast.success(`Status changed to "${InvoiceService.getStatusLabel(bulkStatus)}" for ${result.updated_count} invoices`);
      } else if (result.updated_count > 0) {
        toast.warning(`Status changed for ${result.updated_count} of ${result.total_requested} invoices`);
      } else {
        toast.error('Failed to change status for any invoices');
      }

      setSelectedIds([]);
      setBulkStatus('');
      loadInvoices();
    } catch (err: any) {
      console.error('Failed to bulk change status:', err);
      const message = err?.response?.data?.detail || 'Bulk status change failed';
      toast.error(message);
    } finally {
      setBulkProcessing(false);
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const toggleStatusFilter = (status: StatusFilterOption) => {
    setStatusFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getStatusFilterLabel = (): string => {
    if (statusFilters.length === 0) return 'All statuses';
    if (statusFilters.length === 1) {
      if (statusFilters[0] === 'OVERDUE') return 'Overdue';
      return InvoiceService.getStatusLabel(statusFilters[0]);
    }
    return `${statusFilters.length} selected`;
  };

  const isAllSelected = invoices.length > 0 && selectedIds.length === invoices.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < invoices.length;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
          Invoices ({invoices.length})
        </h3>
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ flex: '1', minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
            Search
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Invoice no., title..."
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
            <FaSearch
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999',
              }}
            />
          </div>
        </div>

        <div style={{ minWidth: '220px', position: 'relative' }} ref={statusDropdownRef}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
            Status
          </label>
          <button
            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: statusFilters.length > 0 ? '1px solid #3b82f6' : '1px solid #e0e3ef',
              borderRadius: '6px',
              fontSize: '14px',
              background: statusFilters.length > 0 ? '#eff6ff' : 'white',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              textAlign: 'left',
            }}
          >
            <span style={{ color: statusFilters.length > 0 ? '#1d4ed8' : '#666' }}>
              {getStatusFilterLabel()}
            </span>
            <FaChevronDown size={12} style={{ color: '#999', transform: statusDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
          {statusDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                background: 'white',
                border: '1px solid #e0e3ef',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                padding: '12px',
                zIndex: 100,
                minWidth: '220px',
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {INVOICE_STATUS_OPTIONS.map((opt) => {
                  const isActive = statusFilters.includes(opt.value);
                  const statusColor = InvoiceService.getStatusColor(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => toggleStatusFilter(opt.value)}
                      style={{
                        padding: '7px 14px',
                        borderRadius: '16px',
                        fontSize: '13px',
                        fontWeight: 600,
                        border: `1px solid ${isActive ? statusColor.text : '#e0e3ef'}`,
                        background: 'white',
                        color: isActive ? statusColor.text : '#666',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
                <button
                  onClick={() => toggleStatusFilter('OVERDUE')}
                  style={{
                    padding: '7px 14px',
                    borderRadius: '16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    border: `1px solid ${statusFilters.includes('OVERDUE') ? '#dc2626' : '#e0e3ef'}`,
                    background: 'white',
                    color: statusFilters.includes('OVERDUE') ? '#dc2626' : '#666',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  Overdue
                </button>
              </div>
              {statusFilters.length > 0 && (
                <button
                  onClick={() => setStatusFilters([])}
                  style={{
                    marginTop: '10px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 500,
                    border: 'none',
                    background: 'transparent',
                    color: '#999',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
            Type
          </label>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as '' | InvoiceKind)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e0e3ef',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
            }}
          >
            <option value="">All types</option>
            <option value="FULL">Schlussrechnung</option>
            <option value="DEPOSIT">Anzahlung</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
              From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                padding: '10px 12px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#666' }}>
              To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                padding: '10px 12px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                fontSize: '14px',
              }}
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              style={{
                padding: '10px 12px',
                border: 'none',
                background: 'transparent',
                color: '#999',
                fontSize: '13px',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            marginBottom: '16px',
            background: '#f0f4ff',
            borderRadius: '8px',
            border: '1px solid #dbeafe',
          }}
        >
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#021a4a' }}>
            {selectedIds.length} selected
          </span>
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value as InvoiceStatus | '')}
            disabled={bulkProcessing}
            style={{
              padding: '8px 12px',
              border: '1px solid #e0e3ef',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white',
              minWidth: '180px',
            }}
          >
            <option value="">Change status to...</option>
            {BULK_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            onClick={handleBulkStatusChange}
            disabled={!bulkStatus || bulkProcessing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              background: bulkStatus && !bulkProcessing ? '#021a4a' : '#ccc',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: bulkStatus && !bulkProcessing ? 'pointer' : 'not-allowed',
            }}
          >
            <FaCheck size={12} />
            {bulkProcessing ? 'Processing...' : 'Apply'}
          </button>
          <button
            onClick={() => {
              setSelectedIds([]);
              setBulkStatus('');
            }}
            disabled={bulkProcessing}
            style={{
              padding: '8px 12px',
              border: '1px solid #e0e3ef',
              borderRadius: '6px',
              background: 'white',
              color: '#666',
              fontSize: '14px',
              cursor: bulkProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          Loading...
        </div>
      ) : invoices.length === 0 ? (
        <div
          style={{
            padding: '60px',
            textAlign: 'center',
            color: '#888',
            background: '#f8f9fc',
            borderRadius: '8px',
          }}
        >
          <p style={{ margin: 0, fontSize: '16px' }}>No invoices found</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            Create invoices from accepted offers.
          </p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8f9fc', borderBottom: '2px solid #e0e3ef' }}>
              <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = isSomeSelected;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
              </th>
              <th style={thStyle}>No.</th>
              <th style={thStyle}>Customer</th>
              <th style={thStyle}>Title</th>
              <th style={{ ...thStyle, width: '100px' }}>Type</th>
              <th style={{ ...thStyle, width: '100px' }}>Status</th>
              <th style={{ ...thStyle, width: '100px' }}>Issue</th>
              <th style={{ ...thStyle, width: '100px' }}>Due</th>
              <th style={{ ...thStyle, width: '120px', textAlign: 'right' }}>Amount</th>
              <th style={{ ...thStyle, width: '80px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => {
              const statusColor = InvoiceService.getStatusColor(invoice.status);
              const overdue = isOverdue(invoice);
              const isSelected = selectedIds.includes(invoice.id);
              return (
                <tr
                  key={invoice.id}
                  style={{
                    borderBottom: '1px solid #e0e3ef',
                    cursor: 'pointer',
                    background: isSelected ? '#f0f4ff' : overdue ? '#fef2f2' : 'transparent',
                  }}
                  onClick={() => handleView(invoice)}
                >
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectOne(invoice.id, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      {invoice.invoice_number}
                    </span>
                  </td>
                  <td style={tdStyle}>{invoice.partner_name || '-'}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: '#021a4a' }}>{invoice.title}</div>
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: invoice.kind === 'DEPOSIT' ? '#fef3c7' : '#e0e7ff',
                        color: invoice.kind === 'DEPOSIT' ? '#92400e' : '#3730a3',
                      }}
                    >
                      {InvoiceService.getKindLabel(invoice.kind)}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: statusColor.bg,
                        color: statusColor.text,
                      }}
                    >
                      {InvoiceService.getStatusLabel(invoice.status)}
                    </span>
                  </td>
                  <td style={tdStyle}>{formatDate(invoice.issue_date)}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {formatDate(invoice.due_date)}
                      {overdue && (
                        <FaExclamationTriangle size={12} color="#dc2626" title="Overdue" />
                      )}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                    {InvoiceService.formatCurrency(invoice.gross_total)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(invoice);
                        }}
                        title="Details"
                        style={actionBtnStyle}
                      >
                        <FaEye size={14} />
                      </button>
                      {invoice.status === 'DRAFT' && (
                        <button
                          onClick={(e) => handleEdit(invoice, e)}
                          title="Edit"
                          style={actionBtnStyle}
                        >
                          <FaEdit size={14} />
                        </button>
                      )}
                      <button
                        onClick={(e) => handleDownloadPdf(invoice, e)}
                        title="PDF"
                        style={actionBtnStyle}
                      >
                        <FaFilePdf size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 600,
  color: '#666',
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '14px',
  color: '#333',
};

const actionBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '6px',
  cursor: 'pointer',
  color: '#666',
  borderRadius: '4px',
};
